import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, BookOpen, User, LogOut, LayoutDashboard, Library } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const navLinkCls = ({ isActive }) =>
  `overline transition-colors duration-200 ${isActive ? "text-brand-ink" : "text-brand-inkMuted hover:text-brand-ink"}`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const { items } = useCart();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 bg-white/75 backdrop-blur-xl border-b border-brand-line">
      <div className="container-narrow flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
          <span className="inline-block w-2 h-2 bg-brand-rust" />
          <span className="font-serif text-xl md:text-2xl tracking-tight text-brand-ink">South on Leadership</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink to="/books" data-testid="nav-books" className={navLinkCls}>Books</NavLink>
          <NavLink to="/courses" data-testid="nav-courses" className={navLinkCls}>Course</NavLink>
          <NavLink to="/field-notes" data-testid="nav-fieldnotes" className={navLinkCls}>Field Notes</NavLink>
          <NavLink to="/free-guide" data-testid="nav-free-guide" className={navLinkCls}>Free Guide</NavLink>
          <NavLink to="/speak" data-testid="nav-speak" className={navLinkCls}>Speak</NavLink>
          <NavLink to="/about" data-testid="nav-about" className={navLinkCls}>About</NavLink>
        </nav>

        <div className="flex items-center gap-4 md:gap-6">
          <Link to="/cart" data-testid="nav-cart" className="relative inline-flex items-center text-brand-ink hover:text-brand-rust transition-colors">
            <ShoppingBag className="w-5 h-5" />
            {items.length > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-3 bg-brand-rust text-white text-[10px] font-semibold w-5 h-5 flex items-center justify-center">
                {items.length}
              </span>
            )}
          </Link>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="account-menu-trigger" className="flex items-center gap-2 text-sm text-brand-ink hover:text-brand-rust transition-colors">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline truncate max-w-[120px]">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-none">
                <DropdownMenuLabel className="overline text-brand-inkMuted">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-library" onClick={() => navigate("/library")}>
                  <Library className="w-4 h-4 mr-2" /> My Library
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" data-testid="nav-login" className="overline text-brand-ink hover:text-brand-rust transition-colors">
              Sign in
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
